import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { ChatBubble } from '@amzn/awsui-chat-components-console';
import { SpaceBetween } from '@amzn/awsui-components-console';
import ChatBubbleAvatar from '../ChatInterface/ChatBubbleAvatar';
import FeedbackActions from '../ChatInterface/FeedbackActions';
import { ThinkingPanel } from '../../chat';
import { toThinkingPanelProps } from '../../chat/adapters';
import { markdownComponents } from '../../chat/markdownComponents';
import { AgentMessageRenderer } from '../ChatInterface/AgentComponents';
import type { AgentChatMessage } from './types';
import type { AgentMessagePart } from '../../../types/agent-messages';

interface AgentMessageItemProps {
  message: AgentChatMessage;
  hideAvatar: boolean;
  userName?: string;
  userInitial?: string;
  onAction?: (action: string) => void;
}

const AgentMessageItem: React.FC<AgentMessageItemProps> = ({
  message,
  hideAvatar,
  userName = 'User',
  userInitial = 'U',
  onAction,
}) => {
  const isUser = message.role === 'user';

  const { thinkingParts, contentParts } = useMemo(() => {
    if (!message.agentParts || message.agentParts.length === 0) {
      return { thinkingParts: [] as AgentMessagePart[], contentParts: [] as AgentMessagePart[] };
    }
    const thinking: AgentMessagePart[] = [];
    const content: AgentMessagePart[] = [];
    for (const part of message.agentParts) {
      if (part.type === 'thinking') {
        thinking.push(part);
      } else {
        content.push(part);
      }
    }
    return { thinkingParts: thinking, contentParts: content };
  }, [message.agentParts]);

  const handleCopy = () => {
    navigator.clipboard.writeText(message.content).catch(() => {});
  };

  if (isUser) {
    return (
      <div className="message-wrapper message-outgoing">
        <ChatBubble
          avatar={
            <ChatBubbleAvatar type="user" name={userName} initials={userInitial} />
          }
          ariaLabel={`${userName} at ${message.timestamp.toLocaleTimeString()}`}
          type="outgoing"
          hideAvatar={hideAvatar}
        >
          <ReactMarkdown components={markdownComponents}>{message.content}</ReactMarkdown>
        </ChatBubble>
      </div>
    );
  }

  return (
    <div className="message-wrapper message-incoming">
      <div className="agent-response-group">
        {thinkingParts.length > 0 && (
          <div className="thinking-section">
            {thinkingParts.map((part, i) => (
              <ThinkingPanel
                key={`thinking-${i}`}
                {...toThinkingPanelProps(part as Extract<AgentMessagePart, { type: 'thinking' }>)}
                expandable={false}
              />
            ))}
          </div>
        )}

        <ChatBubble
          avatar={<ChatBubbleAvatar type="assistant" name="ADDAPT Intelligence" />}
          ariaLabel={`ADDAPT Intelligence at ${message.timestamp.toLocaleTimeString()}`}
          type="incoming"
          hideAvatar={hideAvatar}
          actions={
            <FeedbackActions
              onHelpful={() => {}}
              onNotHelpful={() => {}}
              onCopy={handleCopy}
            />
          }
        >
          {contentParts.length > 0 ? (
            <AgentMessageRenderer parts={contentParts} onAction={onAction} />
          ) : (
            <ReactMarkdown components={markdownComponents}>{message.content}</ReactMarkdown>
          )}
        </ChatBubble>
      </div>
    </div>
  );
};

export default AgentMessageItem;
